import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { api } from '../lib/api';
import { isModuleEnabled } from '../lib/trackingPrefs';
import { workoutDayStreakFromIso } from '../lib/workoutStreak';
import { kgToLbs } from '../lib/units';
import { PremiumCard, PremiumPage, PrimaryButton, SettingsRow } from '../components/premium';
import { NavIcon } from '../components/NavIcons';
import type { UserProfile, Workout } from '../types';
import { appPath } from '../lib/appPaths';
import { openExternalUrl } from '../lib/openExternalUrl';

const goalLabel: Record<string, string> = {
  cut: 'Cut',
  bulk: 'Bulk',
  maintenance: 'Maintain',
};

export function MorePage() {
  const { user, logout } = useAuth();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [streak, setStreak] = useState(0);

  useEffect(() => {
    void api<UserProfile | null>('/api/profile')
      .then(setProfile)
      .catch(() => setProfile(null));
    void api<Workout[]>('/api/workouts')
      .then((w) => setStreak(workoutDayStreakFromIso(w.map((x) => x.startedAt))))
      .catch(() => setStreak(0));
  }, []);

  const showSleep = isModuleEnabled(user, 'sleep');
  const showGoals = isModuleEnabled(user, 'goals');
  const weightDisplay =
    profile?.weightKg != null
      ? user?.preferredUnitSystem === 'imperial'
        ? `${Math.round(kgToLbs(profile.weightKg))} lb`
        : `${Math.round(profile.weightKg)} kg`
      : null;

  return (
    <PremiumPage title="More">
      <PremiumCard variant="hero" className="!p-5">
        <div className="flex items-start gap-4">
          <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-[#00A3FF] to-[#0066FF] text-lg font-bold text-white">
            {(user?.displayName?.trim()?.[0] || user?.email?.[0] || 'U').toUpperCase()}
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="truncate font-display text-xl font-bold text-[var(--text-primary)]">
                  {user?.displayName || user?.email?.split('@')[0] || 'Athlete'}
                </div>
                {profile?.fitnessGoal ? (
                  <div className="mt-0.5 text-sm font-semibold text-accent">
                    Goal: {goalLabel[profile.fitnessGoal] ?? profile.fitnessGoal}
                  </div>
                ) : null}
              </div>
              <Link
                to={appPath('/profile')}
                className="btn-press shrink-0 rounded-full border border-white/15 bg-white/[0.06] px-3 py-1 text-xs font-semibold text-[var(--text-secondary)]"
              >
                Edit
              </Link>
            </div>
            <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs text-[var(--text-muted)]">
              {weightDisplay ? <span>{weightDisplay}</span> : null}
              {profile?.calorieTarget ? <span>{profile.calorieTarget} kcal</span> : null}
              {profile?.proteinTargetG ? <span>{profile.proteinTargetG}g protein</span> : null}
              {streak > 0 ? <span className="text-amber-300">{streak}-day streak</span> : null}
            </div>
          </div>
        </div>
      </PremiumCard>

      <div>
        <p className="mb-2 px-1 text-[11px] font-bold uppercase tracking-widest text-[var(--text-muted)]">Community</p>
        <div className="space-y-2">
          {user?.circlesEnabled ? (
            <SettingsRow
              to="/circles"
              icon={<NavIcon name="circles" className="h-4 w-4 text-violet-300" />}
              label="Circles"
              detail="Private groups for accountability with friends"
            />
          ) : (
            <SettingsRow
              to={appPath('/profile')}
              icon={<NavIcon name="circles" className="h-4 w-4 text-zinc-500" />}
              label="Enable Circles"
              detail="Opt in for friend groups & accountability"
            />
          )}
          <SettingsRow
            to="/leaderboards"
            icon={<NavIcon name="progress" className="h-4 w-4 text-amber-300" />}
            label="Leaderboards"
            detail={
              user?.leaderboardsOptIn
                ? 'Individuals & Circles · you are opted in'
                : 'Individuals & Circles · opt in from Profile'
            }
          />
        </div>
      </div>

      <div>
        <p className="mb-2 px-1 text-[11px] font-bold uppercase tracking-widest text-[var(--text-muted)]">Progress</p>
        <div className="space-y-2">
          {showGoals ? (
            <SettingsRow
              to="/progress"
              icon={<NavIcon name="progress" className="h-4 w-4 text-amber-400" />}
              label="Goals"
              detail="PRs & progress"
            />
          ) : null}
          <SettingsRow
            to="/day-review"
            icon={<NavIcon name="dayReview" className="h-4 w-4 text-amber-300" />}
            label="Day review"
          />
        </div>
      </div>

      <div>
        <p className="mb-2 px-1 text-[11px] font-bold uppercase tracking-widest text-[var(--text-muted)]">Health</p>
        <div className="space-y-2">
          {showSleep ? (
            <SettingsRow
              to="/sleep"
              icon={<NavIcon name="sleep" className="h-4 w-4 text-indigo-300" />}
              label="Sleep"
              detail="Log & trends"
            />
          ) : null}
          <SettingsRow
            to={appPath('/profile')}
            icon={<NavIcon name="profile" className="h-4 w-4" />}
            label="Body & targets"
            detail="Macros, modules, nutrients"
          />
        </div>
      </div>

      <div>
        <p className="mb-2 px-1 text-[11px] font-bold uppercase tracking-widest text-[var(--text-muted)]">App</p>
        <div className="space-y-2">
          <SettingsRow
            to={appPath('/profile')}
            icon={<NavIcon name="settings" className="h-4 w-4" />}
            label="Appearance & modules"
          />
        </div>
      </div>

      <div className="pt-2">
        <p className="mb-2 px-1 text-[11px] font-bold uppercase tracking-widest text-[var(--text-muted)]">Account</p>
        <div className="mb-3 space-y-2">
          <SettingsRow
            onClick={() => void openExternalUrl('https://peakperformanceiq.com/account')}
            icon={<NavIcon name="settings" className="h-4 w-4" />}
            label="Billing & payment"
            detail="Cancel, update card, or invoices on the website"
          />
        </div>
        <PrimaryButton
          variant="danger"
          className="!min-h-[44px] !text-sm"
          onClick={() => {
            logout();
            window.location.assign('/login');
          }}
        >
          Sign out
        </PrimaryButton>
      </div>
    </PremiumPage>
  );
}
